<template>
    <div class="change-line-total-graph-wrap" :class="{ floating: props.isFloatingMode }">
        <div class="inner">
            <div class="inner-line"></div>
            <svg
                ref="svg"
                :width="'100%'"
                :height="BASE_CHART_HEIGHT"
                :viewBox="`0 0 ${effectiveWidth} ${BASE_CHART_HEIGHT}`"
            />
        </div>
        <div class="date-text-wrap body-2--regular">
            <span class="date-text start">1{{ t('all_day') }}</span>
            <span class="date-text end"
                >{{ getDaysInMonth(props.timeseriesOriginData?.Evaluation?.StartDate ?? '') }}{{ t('all_day') }}</span
            >
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import * as d3 from 'd3';
import type { RawData, TimeSeriesResDto } from '@common/openapi-client/openapi';

//import useProReportStore from '@common/trend-report-common/src/stores/proReport';
//import useProReportAdminStore from '@common/trend-report-common/src/stores/proReportAdmin';
import { useTrendReportStore, useTrendReportAdminStore } from '@common/trend-report-common';
import { GRAPH_COLOR_LIST, METRICS_LIST } from '@common/trend-report-common';
import { useI18n } from 'vue-i18n';
import { formatNumberToDecimal } from '@common/common/utils/graphUtils';

const { t } = useI18n();

type ExtendedRawData = RawData & {
    isVirtual?: boolean; // 가상 구간 처리값
    [key: string]: number | string | null | boolean | undefined;
};

const props = defineProps<{
    isFloatingMode: boolean;
    timeseriesOriginData: TimeSeriesResDto | null;
    isAdmin: boolean;
    uid?: string | null;
    screenWidth: number;
    isForShare?: boolean | null;
}>();

const proReportStore = useTrendReportStore();
const proReportAdminStore = useTrendReportAdminStore();

const BASE_WIDTH = 362;
const BASE_HEIGHT = 367;

// isForShare이고 screenWidth가 560 초과일 때 너비 제한
const effectiveWidth = computed(() => {
    if (props.isForShare && props.screenWidth > 560) {
        return 560;
    }
    return props.screenWidth;
});

// 현재 화면에 맞춘 비율 기반 높이
const BASE_CHART_HEIGHT = Math.round(((effectiveWidth.value + 30) / BASE_WIDTH) * BASE_HEIGHT);

const svg = ref<SVGSVGElement | null>(null);

const timeseriesTempData = ref<any | null>(null);
const dayPixelWidth = ref(0);

const computedStore = computed(() => {
    if (props.isAdmin) {
        return proReportAdminStore;
    } else {
        return proReportStore;
    }
});

const chartHeight = computed(() => {
    const totalGap = METRICS_LIST.length - 1;
    return (BASE_CHART_HEIGHT - totalGap) / METRICS_LIST.length;
});

const getDaysInMonth = (monthStr: string): number => {
    const [yearStr, monthStrRaw] = monthStr.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStrRaw, 10); // 1 ~ 12

    if (!year || !month || month < 1 || month > 12) {
        console.warn('Invalid month string:', monthStr);
        return 31; // fallback
    }

    return new Date(year, month, 0).getDate(); // 해당 월의 마지막 날
};

/**
const getAvailableFallbackDate = (
    highlightSet: Set<number>,
    startDay: number,
    maxDay: number,
): number | null => {
    for (let day = startDay; day <= maxDay; day++) {
        if (!highlightSet.has(day)) return day;
    }
    return null;
};
*/

const setInitData = () => {
    const origin = props.timeseriesOriginData?.TimeSeriesData;
    if (!origin) return;

    const raw = origin.RawData ?? [];
    const smooth = origin.SmoothedData ?? [];

    let filledRaw: RawData[] = [...raw];
    let filledSmooth: RawData[] = [...smooth];
    const virtualSmoothed: RawData[] = [];

    if (raw.length > 0 && smooth.length > 0) {
        const firstDate = new Date(raw[0].DATETIMES ?? '');
        const firstDay = firstDate.getDate();
        const year = firstDate.getFullYear();
        const month = firstDate.getMonth(); // 0-based

        if (firstDay > 1) {
            const rawVirtuals: ExtendedRawData[] = [];
            for (let day = 1; day < firstDay; day++) {
                const virtualDate = new Date(year, month, day);
                const clonedRaw = {
                    ...raw[0],
                    DATETIMES: virtualDate.toISOString(),
                    isVirtual: true,
                };
                rawVirtuals.push(clonedRaw);

                const clonedSmooth = {
                    ...smooth[0],
                    DATETIMES: virtualDate.toISOString(),
                    isVirtual: true,
                };
                virtualSmoothed.push(clonedSmooth);
            }

            filledRaw = [...rawVirtuals, ...raw];
            filledSmooth = [...virtualSmoothed, ...smooth];
        }
    }

    timeseriesTempData.value = {
        RawData: filledRaw,
        SmoothedData: filledSmooth,
    };
};

/** 
const setInitData = () => {
    const origin = props.timeseriesOriginData?.TimeSeriesData;
    if (!origin) return;

    const startDate = props.timeseriesOriginData?.Evaluation?.StartDate;

    const [yearStr, monthStr] = props.currentMonthYear.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10); // 1 ~ 12

    const raw = origin.RawData ?? [];
    const smooth = origin.SmoothedData ?? [];

    let filledRaw = [...raw];

    if (raw.length > 0) {
        const firstDate = new Date(raw[0].DATETIMES ?? '');
        const firstDay = firstDate.getDate();

        // 1일부터 시작하지 않으면 가상 데이터 생성
        if (firstDay > 1) {
            const virtualEntries: RawData[] = [];

            for (let day = 1; day < firstDay; day++) {
                const virtualDate = new Date(year, month - 1, day);
                const cloned = {
                    ...raw[0],
                    DATETIMES: virtualDate.toISOString(),
                };
                virtualEntries.push(cloned);
            }

            filledRaw = [...virtualEntries, ...raw];
        }
    }

    timeseriesTempData.value = {
        RawData: filledRaw,
        SmoothedData: smooth,
    };
};
*/

const setGraphSize = () => {
    const startDate = computedStore.value.computedModuleData?.MonthReportMetaInfo?.StartDate;
    if (!startDate) return;

    const totalDaysInMonth = getDaysInMonth(startDate); // 28~31
    const screenWidth = effectiveWidth.value;

    // 너비를 일 수 기준으로 균등 분할
    const pixelPerDay = Number((screenWidth / totalDaysInMonth).toFixed(4));
    dayPixelWidth.value = pixelPerDay;
};

const drawStaticSvg = () => {
    if (!svg.value || !timeseriesTempData.value) return;

    const svgEl = d3.select(svg.value);
    svgEl.selectAll('*').remove();

    const LINE_PADDING = 0.5;

    svgEl
        .append('line')
        .attr('x1', LINE_PADDING)
        .attr('y1', 0)
        .attr('x2', LINE_PADDING)
        .attr('y2', BASE_CHART_HEIGHT)
        .attr('stroke', '#E9ECF0')
        .attr('stroke-width', 1);

    svgEl
        .append('line')
        .attr('x1', effectiveWidth.value - LINE_PADDING)
        .attr('y1', 0)
        .attr('x2', effectiveWidth.value - LINE_PADDING)
        .attr('y2', BASE_CHART_HEIGHT)
        .attr('stroke', '#E9ECF0')
        .attr('stroke-width', 1);

    const { SmoothedData, RawData } = timeseriesTempData.value;
    if (!SmoothedData?.length) return;

    const startDate = props.timeseriesOriginData?.Evaluation?.StartDate;
    if (!startDate) return;

    const totalDays = getDaysInMonth(startDate);
    const width = effectiveWidth.value;

    const xScale = d3.scaleLinear().domain([1, totalDays]).range([0, width]);

    METRICS_LIST.forEach((metric, i) => {
        const startY = i * chartHeight.value;

        const y = d3
            .scaleLinear()
            .domain([
                d3.min([...SmoothedData, ...RawData], d => Number(d[metric])) ?? 0,
                d3.max([...SmoothedData, ...RawData], d => Number(d[metric])) ?? 100,
            ])
            .nice()
            .range([startY + chartHeight.value, startY]);

        const color = GRAPH_COLOR_LIST.find(item => item.metricCode === metric)?.color ?? '#ccc';

        const smoothLine = d3
            .line<ExtendedRawData>()
            .x(d => xScale(new Date(d.DATETIMES!).getDate()))
            .y(d => y(Number(d[metric])))
            .curve(d3.curveMonotoneX);

        const virtualData = SmoothedData.filter((d: ExtendedRawData) => d.isVirtual);
        const realData = SmoothedData.filter((d: ExtendedRawData) => !d.isVirtual);
        const shared = realData.length > 0 ? [realData[0]] : [];
        const extendedVirtual = [...virtualData, ...shared];

        if (extendedVirtual.length > 1) {
            svgEl
                .append('path')
                .datum(extendedVirtual)
                .attr('fill', 'none')
                .attr('stroke', color)
                .attr('stroke-width', 2.5)
                .attr('stroke-dasharray', '4,2')
                .attr('opacity', 0.5)
                .attr('d', smoothLine);
        }

        if (realData.length > 1) {
            svgEl
                .append('path')
                .datum(realData)
                .attr('fill', 'none')
                .attr('stroke', color)
                .attr('stroke-width', 2.5)
                .attr('d', smoothLine);
        }

        if (RawData?.length) {
            const rawLine = d3
                .line<ExtendedRawData>()
                .x(d => xScale(new Date(d.DATETIMES!).getDate()))
                .y(d => y(Number(d[metric])))
                .curve(d3.curveMonotoneX);

            svgEl
                .append('path')
                .datum(RawData)
                .attr('fill', 'none')
                .attr('stroke', color)
                .attr('stroke-width', 1)
                .attr('stroke-dasharray', '4,2')
                .attr('opacity', 0.5)
                .attr('d', rawLine);
        }

        if (realData.length) {
            drawEdgePointsAndLabels({
                svgEl,
                data: realData,
                metric,
                xScale,
                yScale: y,
                color,
            });
        }

        const highlightedMap = props.timeseriesOriginData?.TimeSeriesData?.HighlightedData?.[0] ?? {};

        drawHighlightedPointsAndLabels({
            svgEl,
            data: SmoothedData,
            metric,
            xScale,
            yScale: y,
            color,
            highlightDates: highlightedMap[metric as keyof typeof highlightedMap] ?? [],
        });
    });

    // ✅ Fallback Label (SmoothedData 기준, 메트릭별 개별 판단)
    const fallbackStartDay = 5;
    const highlightedMap = props.timeseriesOriginData?.TimeSeriesData?.HighlightedData?.[0] ?? {};
    const highlightDaySetMap: Record<string, Set<number>> = {};

    METRICS_LIST.forEach(metric => {
        const highlightDays = highlightedMap[metric as keyof typeof highlightedMap] ?? [];
        highlightDaySetMap[metric] = new Set(highlightDays.map(d => new Date(d).getDate()));
    });

    METRICS_LIST.forEach((metric, i) => {
        const smoothed = SmoothedData ?? [];
        const metricColor = GRAPH_COLOR_LIST.find(item => item.metricCode === metric)?.color ?? '#ccc';

        const yScale = d3
            .scaleLinear()
            .domain([
                d3.min([...SmoothedData, ...RawData], d => Number(d[metric])) ?? 0,
                d3.max([...SmoothedData, ...RawData], d => Number(d[metric])) ?? 100,
            ])
            .nice()
            .range([i * chartHeight.value + chartHeight.value, i * chartHeight.value]);

        const xScale = d3.scaleLinear().domain([1, totalDays]).range([0, width]);

        const highlightSet = highlightDaySetMap[metric];

        let fallbackDatum: ExtendedRawData | undefined;
        let availableDay: number | null = null;

        for (let dayOffset = 0; dayOffset + fallbackStartDay <= totalDays; dayOffset++) {
            const candidateDay = fallbackStartDay + dayOffset;

            if (highlightSet.has(candidateDay)) continue;

            const candidateDatum = smoothed.find(
                (d: ExtendedRawData) => new Date(d.DATETIMES ?? '').getDate() === candidateDay,
            );

            if (candidateDatum) {
                fallbackDatum = candidateDatum;
                availableDay = candidateDay;
                break;
            }
        }

        if (!fallbackDatum || !availableDay) return;

        const metricInfo = GRAPH_COLOR_LIST.find(item => item.metricCode === metric);
        const labelText = metricInfo?.metricText ?? metric;

        // ⚠️ 여기서 개별 위치 판단 (기본 위, 공간 부족 시 아래)
        const y = yScale(Number(fallbackDatum[metric]));
        const labelYTop = y - 12; // 기본 위 위치
        const yTopLimit = yScale.range()[1]; // SVG 기준 상단 (작은 수)

        const placeBelow = labelYTop - 12 < yTopLimit; // 공간 없으면 아래
        drawFallbackLabel({
            svgEl,
            rawDatum: fallbackDatum,
            metric,
            xScale,
            yScale,
            color: metricColor,
            labelText,
            placeBelow, // ✅ 개별 판단된 방향
        });
    });
};

const drawFallbackLabel = (params: {
    svgEl: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    rawDatum: ExtendedRawData;
    metric: string;
    xScale: d3.ScaleLinear<number, number>;
    yScale: d3.ScaleLinear<number, number>;
    color: string;
    labelText: string;
    placeBelow: boolean;
}) => {
    const { svgEl, rawDatum, metric, xScale, yScale, color, labelText, placeBelow } = params;

    const TEXT_HEIGHT = 10;
    const TEXT_MARGIN = 24;
    const PADDING_X = 5;
    const PADDING_Y = 0;
    const BORDER_RADIUS = 5;

    const value = rawDatum[metric];
    if (value === null || value === undefined) return;

    const day = new Date(rawDatum.DATETIMES!).getDate();
    const x = xScale(day);
    const y = yScale(Number(value));

    const textY = placeBelow ? y + TEXT_MARGIN + TEXT_HEIGHT : y - TEXT_MARGIN;

    // 연결 점선
    svgEl
        .append('line')
        .attr('x1', x)
        .attr('y1', y)
        .attr('x2', x)
        .attr('y2', placeBelow ? textY - TEXT_HEIGHT + 2 : textY - 2)
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,2');

    // 배경 박스
    const labelGroup = svgEl.append('g');

    const textEl = labelGroup
        .append('text')
        .attr('x', x)
        .attr('y', textY)
        .attr('fill', color)
        .attr('font-size', `${TEXT_HEIGHT}px`)
        .attr('font-weight', 'bold')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .text(labelText);

    const bbox = (textEl.node() as SVGTextElement).getBBox();

    labelGroup
        .insert('rect', 'text')
        .attr('x', bbox.x - PADDING_X)
        .attr('y', bbox.y - PADDING_Y)
        .attr('width', bbox.width + PADDING_X * 2)
        .attr('height', bbox.height + PADDING_Y * 2)
        .attr('fill', '#fff')
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('rx', BORDER_RADIUS)
        .attr('ry', BORDER_RADIUS);
};

const drawEdgePointsAndLabels = (params: {
    svgEl: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    data: ExtendedRawData[];
    metric: string;
    xScale: d3.ScaleLinear<number, number>;
    yScale: d3.ScaleLinear<number, number>;
    color: string;
}) => {
    const { svgEl, data, metric, xScale, yScale, color } = params;
    if (!data?.length) return;

    const firstDatum = data[0];
    const lastDatum = data[data.length - 1];

    const TEXT_FIXED_OFFSET = 12;
    const TEXT_MARGIN = 4;

    const [_, yTop] = yScale.range().sort((a, b) => b - a); // yTop: 위쪽 작은 값

    const getTextY = (y: number): number => {
        const minAllowedY = yTop + TEXT_FIXED_OFFSET + TEXT_MARGIN;
        const shouldGoBelow = y < minAllowedY;
        return shouldGoBelow
            ? y + TEXT_FIXED_OFFSET + TEXT_MARGIN // 아래로
            : y - TEXT_FIXED_OFFSET; // 위로 고정
    };

    const startX = xScale(new Date(firstDatum.DATETIMES ?? '').getDate());
    const startY = yScale(Number(firstDatum[metric] ?? 0));
    const startTextY = getTextY(startY);

    const endX = xScale(new Date(lastDatum.DATETIMES ?? '').getDate());
    const endY = yScale(Number(lastDatum[metric] ?? 0));
    const endTextY = getTextY(endY);

    // 시작점 원
    svgEl
        .append('circle')
        .attr('cx', startX)
        .attr('cy', startY)
        .attr('r', 3)
        .attr('fill', '#fff')
        .attr('stroke', color)
        .attr('stroke-width', 1);

    // 시작점 텍스트
    const firstValue = Number(firstDatum[metric] ?? 0);
    const formattedFirstValue =
        metric === 'Fitscore' || metric === 'FitScore' ? firstValue.toString() : formatNumberToDecimal(firstValue);
    svgEl
        .append('text')
        .attr('x', startX)
        .attr('y', startTextY)
        .attr('fill', color)
        .attr('font-size', '12px')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .text(formattedFirstValue);

    // 끝점 원
    svgEl
        .append('circle')
        .attr('cx', endX)
        .attr('cy', endY)
        .attr('r', 3)
        .attr('fill', '#fff')
        .attr('stroke', color)
        .attr('stroke-width', 1);

    // 끝점 텍스트
    const lastValue = Number(lastDatum[metric] ?? 0);
    const formattedLastValue =
        metric === 'Fitscore' || metric === 'FitScore' ? lastValue.toString() : formatNumberToDecimal(lastValue);
    svgEl
        .append('text')
        .attr('x', endX)
        .attr('y', endTextY)
        .attr('fill', color)
        .attr('font-size', '12px')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .text(formattedLastValue);
};

const drawHighlightedPointsAndLabels = (params: {
    svgEl: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    data: ExtendedRawData[];
    metric: string;
    xScale: d3.ScaleLinear<number, number>;
    yScale: d3.ScaleLinear<number, number>;
    color: string;
    highlightDates: string[];
}) => {
    const { svgEl, data, metric, xScale, yScale, color, highlightDates } = params;

    const highlightSet = new Set(highlightDates.map(d => new Date(d).getDate()));

    const TEXT_OFFSET = 12;
    const TEXT_MARGIN = 4;

    // yTop: SVG 기준 위쪽 위치 (작은 숫자)
    const [_, yTop] = yScale.range().sort((a, b) => b - a);

    const getTextY = (y: number): number => {
        const minAllowedY = yTop + TEXT_OFFSET + TEXT_MARGIN;
        return y < minAllowedY
            ? y + TEXT_OFFSET // 공간 부족 → 아래로
            : y - TEXT_OFFSET; // 충분한 공간 → 위로 고정
    };

    data.forEach(d => {
        const date = new Date(d.DATETIMES ?? '');
        const day = date.getDate();

        // 하이라이트에 포함 안되면 skip
        if (!highlightSet.has(day)) return;

        // 시작 혹은 마지막 날짜는 제외
        const firstDate = new Date(data[0].DATETIMES ?? '');
        const lastDate = new Date(data[data.length - 1].DATETIMES ?? '');
        if (date.toDateString() === firstDate.toDateString() || date.toDateString() === lastDate.toDateString()) {
            return;
        }

        const value = d[metric];
        if (value === null || value === undefined) return;

        const x = xScale(day);
        const y = yScale(Number(value));
        const textY = getTextY(y);

        // 원 (하이라이트 포인트)
        svgEl
            .append('circle')
            .attr('cx', x)
            .attr('cy', y)
            .attr('r', 3)
            .attr('fill', '#fff')
            .attr('stroke', color)
            .attr('stroke-width', 1);

        // 텍스트 (값)
        const numValue = Number(value);
        const formattedValue =
            metric === 'Fitscore' || metric === 'FitScore' ? numValue.toString() : formatNumberToDecimal(numValue);
        svgEl
            .append('text')
            .attr('x', x)
            .attr('y', textY)
            .attr('fill', color)
            .attr('font-size', `${TEXT_OFFSET}px`)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'central')
            .text(formattedValue);
    });
};

onMounted(async () => {
    setInitData();
    await nextTick();

    requestAnimationFrame(async () => {
        await setGraphSize();
        await drawStaticSvg(); // 최초 1회만 실행
    });
});
</script>

<style scoped lang="scss">
.change-line-total-graph-wrap {
    &.floating {
        padding-bottom: 0;
    }
}

.inner {
    width: 100%;
    height: 100%;
    position: relative;
    background-color: $primary-white;
    box-sizing: border-box;

    svg {
        position: relative;
        z-index: 2;
        overflow: visible;
    }

    .inner-line {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 1px;
        height: 100%;
        background-color: $grey-1;
    }

    &:before,
    &:after {
        position: absolute;
        content: '';
        display: block;
        width: 1px;
        height: 100%;
        background-color: $grey-1;
    }

    &:before {
        left: 25%;
        top: 0;
    }

    &:after {
        right: 25%;
        top: 0;
    }
}

.highlight-rect.dimmed {
    fill: rgba(255, 255, 255, 0.6);
}

.evaluation-title-box.dimmed,
.evaluation-background-box.dimmed {
    fill: white; // 딤 처리할 때 색깔 지정
    opacity: 0.6; // 약간 흐리게
}

.graph-info-list {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 1.5rem;

    .bottom-date {
        font-size: 1.2rem;
        line-height: 170%;
        color: $grey-7;
    }

    .legend-box {
        display: flex;
        align-items: center;
        background-color: $grey-1;
        border-radius: 0.4rem;
        padding: 0 0.5rem;
        margin-top: 0.5rem;

        .legend-item {
            display: flex;
            align-items: center;

            & + .legend-item {
                margin-left: 0.7rem;
            }

            .legend-item-color {
                width: 0.4rem;
                height: 0.4rem;
                border-radius: 50%;
                margin-right: 0.2rem;
            }

            .legend-item-text {
                line-height: 150%;
            }
        }
    }
}

.date-text-wrap {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $grey-7;
    font-size: 1.5rem;
    padding: 0 1.5rem;

    .date-text {
        font-size: 1.2rem;
    }
}
</style>
